import { addBreadcrumb, trackPerformance, logErrorWithContext } from './sentrySetup';

/**
 * Options for tracked fetch
 */
interface TrackedFetchOptions extends RequestInit {
  /** Optional custom operation name for tracking */
  operationName?: string;
}

/**
 * Extract operation name from URL for tracking purposes
 */
const extractOperationName = (url: string): string => {
  try {
    // Handle both absolute and relative URLs
    // In React Native, window.location may not exist
    const baseUrl =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://placeholder.local';
    const urlObj = new URL(url, baseUrl);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    // Get last 2 meaningful path segments
    const meaningfulParts = pathParts.slice(-2).join('/');
    return meaningfulParts || 'unknown';
  } catch {
    return 'unknown';
  }
};

/**
 * Tracked fetch wrapper that adds Sentry breadcrumbs and performance tracking
 */
export const trackedFetch = async (url: string, options: TrackedFetchOptions = {}): Promise<Response> => {
  const { operationName, ...fetchOptions } = options;
  const operation = operationName || extractOperationName(url);
  const method = (fetchOptions.method || 'GET').toUpperCase();
  const startTime = Date.now();

  // Add breadcrumb for request start
  addBreadcrumb('api', `${method} ${operation} started`, 'info', {
    url,
    method
  });

  try {
    const response = await fetch(url, fetchOptions);
    const duration = Date.now() - startTime;

    // Track performance for slow requests
    trackPerformance(`api_${operation}`, { duration, success: response.ok });

    // Add breadcrumb for response
    addBreadcrumb('api', `${method} ${operation} completed`, response.ok ? 'info' : 'warning', {
      url,
      method,
      status: response.status,
      duration_ms: duration
    });

    // Log errors for failed requests (4xx, 5xx)
    if (response.status >= 400) {
      logErrorWithContext(new Error(`API request failed: ${method} ${operation} - ${response.status}`), 'api_error', {
        url,
        method,
        status: response.status,
        duration_ms: duration
      });
    }

    return response;
  } catch (error) {
    const duration = Date.now() - startTime;

    // Add breadcrumb for network error
    addBreadcrumb('api', `${method} ${operation} failed`, 'error', {
      url,
      method,
      duration_ms: duration,
      error: error instanceof Error ? error.message : String(error)
    });

    // Log network errors
    logErrorWithContext(error, 'api_network_error', {
      url,
      method,
      duration_ms: duration
    });

    throw error;
  }
};

/**
 * Create a tracked fetch with retry support
 */
export const createTrackedFetchRetry = (maxRetries = 3, retryDelay = 1000) => {
  return async (url: string, options: TrackedFetchOptions = {}): Promise<Response> => {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await trackedFetch(url, {
          ...options,
          operationName: options.operationName ? `${options.operationName} (attempt ${attempt})` : undefined
        });

        // Don't retry on client errors (4xx), only on server errors (5xx) or network issues
        if (response.status < 500 && response.status !== 408 && response.status !== 429) {
          return response;
        }

        // If it's a server error, we might want to retry
        if (attempt < maxRetries) {
          await new Promise(resolve => {
            setTimeout(resolve, retryDelay * attempt);
          });
          continue;
        }

        return response;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (attempt < maxRetries) {
          await new Promise(resolve => {
            setTimeout(resolve, retryDelay * attempt);
          });
          continue;
        }
      }
    }

    throw lastError || new Error('Request failed after retries');
  };
};
